import { useCallback, useEffect, useRef, useState } from 'react';
import type { RevealFlag } from '../data/types';

/* Round progress lives entirely in the URL hash: #<index>-<flags>, one character per item
   ('.' unseen, 'r' revealed real, 'f' revealed fake). Example: #4-rrrrf.....

   This is not over-engineering. Revealing a REAL headline NAVIGATES AWAY to the news article,
   so in-memory state is gone the moment Adorian clicks. The hash survives that round trip,
   plus refresh and the Back button. sessionStorage would survive too now this is served over
   https, but the hash is what the browser hands back on Back, so it stays the single source of
   truth rather than a second copy to keep in sync. */

export interface RoundProgress {
  index: number;
  flags: RevealFlag[];
  realCount: number;
  fakeCount: number;
  goTo: (next: number) => void;
  markRevealed: (verdict: Exclude<RevealFlag, null>) => void;
  /** Re-read the hash — used when the browser restores this page from bfcache. */
  resync: () => void;
}

interface HashState {
  index: number;
  flags: RevealFlag[];
}

export function readHash(count: number, hash = window.location.hash): HashState {
  const raw = hash.replace('#', '');
  const [rawIndex, rawFlags = ''] = raw.split('-');
  const parsed = Number.parseInt(rawIndex, 10);
  const index = Number.isFinite(parsed) && parsed >= 0 && parsed < count ? parsed : 0;
  const flags: RevealFlag[] = Array.from({ length: count }, (_, n) => {
    const c = rawFlags[n];
    return c === 'r' ? 'real' : c === 'f' ? 'fake' : null;
  });
  return { index, flags };
}

export function encodeHash(index: number, flags: RevealFlag[]): string {
  const encoded = flags.map((f) => (f === 'real' ? 'r' : f === 'fake' ? 'f' : '.')).join('');
  return `#${index}-${encoded}`;
}

export function useRoundProgress(count: number): RoundProgress {
  const [state, setState] = useState<HashState>(() => readHash(count));

  /* The ref, not the state variable, is the value we compute from.
     `markRevealed` has to write the hash and then immediately be followed by a history push
     and a navigation — so the write must be SYNCHRONOUS. Doing it inside a setState updater
     would defer it (updaters run during render in concurrent mode), and the verdict could
     miss the URL before the browser left the page. Ref first, then setState with a value. */
  const ref = useRef(state);

  const apply = useCallback((next: HashState) => {
    ref.current = next;
    // replaceState, not push: stepping between headlines must not fill history, or Back from
    // the article would walk backwards through every card he looked at.
    window.history.replaceState(null, '', encodeHash(next.index, next.flags));
    setState(next);
  }, []);

  const goTo = useCallback(
    (next: number) => {
      if (next < 0 || next >= count) return;
      apply({ ...ref.current, index: next });
    },
    [apply, count],
  );

  const markRevealed = useCallback(
    (verdict: Exclude<RevealFlag, null>) => {
      const current = ref.current;
      const flags = current.flags.slice();
      flags[current.index] = verdict;
      // Bakes the verdict into the URL before anything navigates away, so Back restores a
      // tally that already includes this reveal.
      apply({ ...current, flags });
    },
    [apply],
  );

  const resync = useCallback(() => {
    const next = readHash(count);
    ref.current = next;
    setState(next);
  }, [count]);

  // Normalise the URL on mount, and rebuild the flag string if the round length changes.
  useEffect(() => {
    apply(readHash(count));
  }, [apply, count]);

  return {
    index: state.index,
    flags: state.flags,
    realCount: state.flags.filter((f) => f === 'real').length,
    fakeCount: state.flags.filter((f) => f === 'fake').length,
    goTo,
    markRevealed,
    resync,
  };
}
